import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  Text,
  Stack,
  Button,
  Loader,
  Center,
  Badge,
  SimpleGrid,
  Modal,
  TextInput
} from '@mantine/core'
import { useParams, useHistory } from 'react-router-dom'
import agent from '../../app/api/agent'
import { CartridgeSpecDto, AmmoProductDto } from '../../app/models/ammo'
import { ReloaderRecipeDto } from '../../app/models/reloaderRecipe'
import { PagedResult } from '../../app/models/pagedResult'
import { useStore } from '../../app/stores/store'
import { observer } from 'mobx-react-lite'
import { toast } from 'react-toastify'

const CartridgeSpecDetail = observer(() => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const { userStore } = useStore()
  const [spec, setSpec] = useState<CartridgeSpecDto | null>(null)
  const [products, setProducts] = useState<AmmoProductDto[]>([])
  const [recipes, setRecipes] = useState<ReloaderRecipeDto[]>([])
  const [loading, setLoading] = useState(true)
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)

  useEffect(() => {
    Promise.all([
      agent.CartridgeSpecs.details(id),
      agent.AmmoProducts.list({ cartridgeSpecId: id, pageSize: 100 }),
      agent.ReloaderRecipes.list({ cartridgeSpecId: id, pageSize: 20 })
    ])
      .then(([cartridgeSpec, productsResult, recipesResult]: [CartridgeSpecDto, PagedResult<AmmoProductDto>, PagedResult<ReloaderRecipeDto>]) => {
        setSpec(cartridgeSpec)
        setProducts(productsResult.items)
        setRecipes(recipesResult.items)
      })
      .finally(() => setLoading(false))
  }, [id])

  const handleApprove = async () => {
    if (!spec) return
    try {
      await agent.CartridgeSpecs.approve(spec.id)
      toast.success('Cartridge spec approved')
    } catch (error) {
      toast.error('Failed to approve')
    }
  }

  const handleReject = async () => {
    if (!spec) return
    try {
      await agent.CartridgeSpecs.reject(spec.id, rejectReason)
      toast.success('Cartridge spec rejected')
      setShowRejectModal(false)
    } catch (error) {
      toast.error('Failed to reject')
    }
  }

  if (loading) {
    return (
      <Center>
        <Loader />
      </Center>
    )
  }

  if (!spec) { return <Text>Spec not found</Text> }

  return (
    <Box sx={{ background: 'var(--gs-bg)', minHeight: '100vh', paddingTop: '24px', paddingBottom: '24px' }}>
      <Container size="md">
        <Stack spacing="md">
          {/* Back Link */}
          <Button
            variant="subtle"
            onClick={() => history.push('/ammo')}
            sx={{ width: 'fit-content', paddingLeft: 0 }}
          >
            ← Back to Ammo Registry
          </Button>

          {/* Header */}
          <Box>
            <Text
              sx={{
                fontSize: '28px',
                fontWeight: 700,
                marginBottom: '8px'
              }}
            >
              {spec.name}
            </Text>
            <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[spec.shortName, spec.caliber, spec.type].filter(Boolean).map((value) => (
                <Badge key={value} variant="outline" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {value}
                </Badge>
              ))}
            </Box>
          </Box>

          {/* Admin Panel */}
          {userStore.user?.isAdmin && (
            <Box
              sx={{
                padding: '16px',
                background: 'var(--gs-surface2)',
                border: '1px solid var(--gs-border)',
                borderRadius: '6px'
              }}
            >
              <Text weight={700} size="sm" sx={{ marginBottom: '12px' }}>
                Admin Actions
              </Text>
              <Button.Group>
                <Button size="sm" onClick={handleApprove}>
                  Approve
                </Button>
                <Button size="sm" color="red" onClick={() => setShowRejectModal(true)}>
                  Reject
                </Button>
              </Button.Group>
            </Box>
          )}

          {/* Products */}
          <Box>
            <Text weight={700} size="lg" sx={{ marginBottom: '12px' }}>
              Loads ({products.length})
            </Text>
            {products.length === 0
              ? (
              <Box
                sx={{
                  padding: '24px',
                  textAlign: 'center',
                  border: '1px dashed var(--gs-border)',
                  borderRadius: '6px'
                }}
              >
                <Text sx={{ color: 'var(--gs-muted)' }}>
                  No loads for this cartridge yet.
                </Text>
              </Box>
                )
              : (
              <SimpleGrid cols={2} spacing="sm" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                {products.map((product) => (
                  <Box
                    key={product.id}
                    onClick={() => history.push(`/ammo/products/${product.id}`)}
                    sx={{
                      padding: '12px',
                      border: '1px solid var(--gs-border)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: 'var(--gs-surface)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#1A3A5C',
                        background: 'var(--gs-surface2)'
                      }
                    }}
                  >
                    <Text weight={700} size="sm" sx={{ marginBottom: '4px' }}>
                      {product.brand} {product.name}
                    </Text>
                    <Text
                      size="xs"
                      sx={{
                        color: 'var(--gs-text)',
                        fontFamily: 'JetBrains Mono, monospace'
                      }}
                    >
                      {product.grainWeight}gr {product.bulletType}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
                )}
          </Box>

          {/* Reloader Recipes */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <Text weight={700} size="lg">
                Community Recipes ({recipes.length})
              </Text>
              {recipes.length > 0 && (
                <Button size="xs" variant="light" onClick={() => history.push(`/ammo/reloading?cartridgeSpecId=${id}`)}>
                  View All
                </Button>
              )}
            </Box>
            {recipes.length === 0
              ? (
              <Box
                sx={{
                  padding: '24px',
                  textAlign: 'center',
                  border: '1px dashed var(--gs-border)',
                  borderRadius: '6px'
                }}
              >
                <Text sx={{ color: 'var(--gs-muted)', marginBottom: '12px' }}>
                  No recipes yet for this cartridge.
                </Text>
                <Button size="sm" onClick={() => history.push('/ammo/reloading/new')} color="ember">
                  Post a Recipe
                </Button>
              </Box>
                )
              : (
              <SimpleGrid cols={1} spacing="sm">
                {recipes.slice(0, 3).map((recipe) => (
                  <Box
                    key={recipe.id}
                    onClick={() => history.push(`/ammo/reloading/${recipe.id}`)}
                    sx={{
                      padding: '12px',
                      border: '1px solid var(--gs-border)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: 'var(--gs-surface)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#1A3A5C',
                        background: 'var(--gs-surface2)'
                      }
                    }}
                  >
                    <Text weight={600} size="sm" sx={{ marginBottom: '4px' }}>
                      {recipe.powderBrand} {recipe.powderType}
                    </Text>
                    <Text size="xs" sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--gs-text)' }}>
                      {recipe.chargeWeightGrMin}-{recipe.chargeWeightGrMax}gr | {recipe.bulletWeightGr}gr {recipe.bulletType}
                    </Text>
                    <Text size="xs" color="dimmed" mt={4}>
                      {recipe.validationCount} validations • {recipe.authorDisplayName || recipe.authorUsername}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
                )}
          </Box>

          {/* Compatible Firearms */}
          <Box>
            <Text weight={700} size="lg" sx={{ marginBottom: '12px' }}>
              Compatible Firearms
            </Text>
            {spec && spec.compatibleFirearms && spec.compatibleFirearms.length > 0
              ? (
              <SimpleGrid cols={2} spacing="sm" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                {spec.compatibleFirearms.map((fw) => (
                  <Box
                    key={fw.id}
                    onClick={() => history.push(`/registry/${fw.id}`)}
                    sx={{
                      padding: '12px',
                      border: '1px solid var(--gs-border)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      background: 'var(--gs-surface)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#1A3A5C',
                        background: 'var(--gs-surface2)'
                      }
                    }}
                  >
                    <Text weight={700} size="sm" sx={{ marginBottom: '4px' }}>
                      {fw.make} {fw.model}
                    </Text>
                    {fw.platform && (
                      <Text
                        size="xs"
                        sx={{
                          color: 'var(--gs-text)',
                          fontFamily: 'JetBrains Mono, monospace'
                        }}
                      >
                        {fw.platform}
                      </Text>
                    )}
                  </Box>
                ))}
              </SimpleGrid>
                )
              : (
              <Box
                sx={{
                  padding: '24px',
                  textAlign: 'center',
                  border: '1px dashed var(--gs-border)',
                  borderRadius: '6px'
                }}
              >
                <Text sx={{ color: 'var(--gs-muted)' }}>
                  No compatible firearms data available.
                </Text>
              </Box>
                )}
          </Box>
        </Stack>
      </Container>

      {/* Reject Modal */}
      <Modal
        opened={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Cartridge Spec"
      >
        <Stack spacing="md">
          <TextInput
            label="Reason (optional)"
            placeholder="Why is this being rejected?"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
          />
          <Button.Group position="right">
            <Button variant="subtle" onClick={() => setShowRejectModal(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleReject}>
              Reject
            </Button>
          </Button.Group>
        </Stack>
      </Modal>
    </Box>
  )
})

export default CartridgeSpecDetail
